import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ErreurApi, type AnalyseImport, type CorpsImport, type LotImport, type MethodeLeve } from './api';
import { Icone } from './icones';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * IMPORT SIG — le géomètre dépose son fichier (D.926)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️⚠️ CET ÉCRAN EXISTE PARCE QUE LE SERVICE SAVAIT DÉJÀ LE FAIRE. Mesuré le
 * 23/09/2026, après la mise en service : `/imports/sig/analyser` et
 * `/imports/sig/deposer` répondaient en production, et
 * `grep -rl "imports/sig" frontend/src` rendait ZÉRO. Le fondateur l'a dit en
 * une phrase : « un géomètre ne peut pas encore déposer son .dxf en cliquant ».
 * C'est le motif « présent et inatteignable », déjà payé sur `verifier-emprise`
 * et sur `exports/parcelles`.
 *
 * ⚠️ L'ANALYSE PRÉCÈDE LE DÉPÔT, ET L'ÉCRAN L'IMPOSE. Le bouton « Déposer »
 * n'apparaît qu'après une analyse réussie. Ce n'est pas une politesse : sans
 * ce temps, le seul moyen de savoir ce qu'un fichier contient est de l'avoir
 * déjà déposé — et retirer quatre cents levés se fait un par un.
 *
 * ⚠️ LES RECOUVREMENTS SONT EN TÊTE, PAS DANS LA LISTE. Noyés au vingtième
 * rang, ils se lisent après le dépôt. En tête, ils arrêtent la main.
 *
 * ⚠️ NI LA MÉTHODE NI LA PRÉCISION NE VIENNENT DU FICHIER. Aucun des deux
 * formats ne les porte. L'agent les DÉCLARE, et l'écran le dit : un `.dxf`
 * reçu par courriel n'est pas un levé GNSS RTK, et un défaut le ferait croire.
 */

/* ⚠️ PAS DE MACHINE À ÉTATS ICI, ET C'EST DÉLIBÉRÉ. Ma première rédaction
   posait un `type Etat = 'vide' | 'lu' | 'analyse' | 'depose'` que rien ne
   LISAIT : `tsc` l'a dit (TS6133). Le pilotage se fait par ce qui existe —
   `format` dit si un fichier est lu, `analyse` si elle a eu lieu, `depot` si
   le dépôt est fait. Un état parallèle aux données se désynchronise d'elles. */

const METHODES: Array<{ cle: MethodeLeve; libelle: string }> = [
  { cle: 'station_totale', libelle: 'Station totale' },
  { cle: 'gnss_rtk', libelle: 'GNSS RTK' },
  { cle: 'gnss_statique', libelle: 'GNSS statique' },
  { cle: 'gps_navigation', libelle: 'GPS de navigation' },
  { cle: 'photo_interpretation', libelle: 'Photo-interprétation' },
];

/** Les deux fuseaux du pays. Le DXF n'en porte aucun : il faut choisir. */
const FUSEAUX = [
  { epsg: 32628, libelle: 'UTM 28N — Conakry, Boké, la côte' },
  { epsg: 32629, libelle: 'UTM 29N — Kankan, Nzérékoré, l’intérieur' },
  { epsg: 4326, libelle: 'WGS 84 — degrés' },
];

function versBase64(f: File): Promise<string> {
  return new Promise((resolve, rejeter) => {
    const l = new FileReader();
    l.onerror = () => rejeter(new Error(`Lecture de ${f.name} impossible`));
    l.onload = () => {
      const r = String(l.result);
      // `data:...;base64,XXXX` — on ne garde que la charge.
      resolve(r.slice(r.indexOf(',') + 1));
    };
    l.readAsDataURL(f);
  });
}

function versTexte(f: File): Promise<string> {
  return new Promise((resolve, rejeter) => {
    const l = new FileReader();
    l.onerror = () => rejeter(new Error(`Lecture de ${f.name} impossible`));
    l.onload = () => resolve(String(l.result));
    l.readAsText(f);
  });
}

export function ImportSig({ onOuvrirParcelle }: { onOuvrirParcelle?: (id: string) => void }) {
  const [corps, setCorps] = useState<CorpsImport>({});
  const [nomsFichiers, setNomsFichiers] = useState<Record<string, string>>({});
  const [analyse, setAnalyse] = useState<AnalyseImport | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [lots, setLots] = useState<LotImport[]>([]);

  // Ce que l'agent déclare — et que le fichier ne porte pas.
  const [lotReference, setLotReference] = useState('');
  const [methode, setMethode] = useState<MethodeLeve>('station_totale');
  const [precision, setPrecision] = useState('0.10');
  const [dossier, setDossier] = useState('');
  const [malgreRecouvrements, setMalgre] = useState(false);
  const [depot, setDepot] = useState<string | null>(null);

  const rafraichirLots = useCallback(() => {
    api.imports.lots().then((r) => setLots(r.lots)).catch(() => setLots([]));
  }, []);
  useEffect(rafraichirLots, [rafraichirLots]);

  const format = corps.dxf ? 'dxf' : corps.shpBase64 ? 'shapefile' : null;

  const prendreFichiers = useCallback(async (fichiers: FileList | null) => {
    if (!fichiers || fichiers.length === 0) return;
    setErreur(null); setDetail(null); setAnalyse(null); setDepot(null);
    const suivant: CorpsImport = { ...corps };
    const noms: Record<string, string> = { ...nomsFichiers };
    try {
      for (const f of Array.from(fichiers)) {
        const ext = f.name.toLowerCase().split('.').pop() ?? '';
        if (ext === 'shp') { suivant.shpBase64 = await versBase64(f); suivant.dxf = undefined; noms.shp = f.name; }
        else if (ext === 'dbf') { suivant.dbfBase64 = await versBase64(f); noms.dbf = f.name; }
        else if (ext === 'prj') { suivant.prj = await versTexte(f); noms.prj = f.name; }
        else if (ext === 'dxf') { suivant.dxf = await versTexte(f); suivant.shpBase64 = undefined; noms.dxf = f.name; }
        else {
          /* ⚠️ ON NOMME CE QU'ON ÉCARTE. Un `.shx` ou un `.cpg` glissé avec le
             reste serait ignoré en silence, et l'agent croirait l'avoir donné. */
          setDetail(`« ${f.name} » n’est ni .shp, ni .dbf, ni .prj, ni .dxf — il n’a pas été lu.`);
        }
      }
      setCorps(suivant); setNomsFichiers(noms);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Lecture impossible');
    }
  }, [corps, nomsFichiers]);

  const analyser = useCallback(async () => {
    setOccupe(true); setErreur(null); setDetail(null);
    try {
      const r = await api.imports.analyser(corps);
      setAnalyse(r);
    } catch (e) {
      setAnalyse(null);
      if (e instanceof ErreurApi) {
        setErreur(e.message);
        const d = (e.details as { detail?: string } | undefined)?.detail;
        if (d) setDetail(d);
      } else setErreur('Analyse impossible');
    } finally { setOccupe(false); }
  }, [corps]);

  const deposer = useCallback(async () => {
    setOccupe(true); setErreur(null);
    try {
      const r = await api.imports.deposer({
        ...corps,
        lotReference: lotReference.trim(),
        methode,
        precisionAnnonceeM: Number(precision),
        dossierReference: dossier.trim() || undefined,
        malgreRecouvrements,
      });
      setDepot(`${r.deposes} levé(s) déposé(s)${r.refuses > 0 ? `, ${r.refuses} refusé(s)` : ''} — ${r.leves.map((l) => l.reference).join(', ')}`); rafraichirLots();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Dépôt impossible');
    } finally { setOccupe(false); }
  }, [corps, lotReference, methode, precision, dossier, malgreRecouvrements, rafraichirLots]);

  const recommencer = useCallback(() => {
    setCorps({}); setNomsFichiers({}); setAnalyse(null);
    setErreur(null); setDetail(null); setDepot(null); setLotReference('');
  }, []);

  const prêtÀDéposer = useMemo(
    () => analyse !== null && lotReference.trim().length >= 4 && Number(precision) > 0
          && (analyse.entitesEnRecouvrement === 0 || malgreRecouvrements),
    [analyse, lotReference, precision, malgreRecouvrements]);

  return (
    <div className="imp-ecran">
      <header className="imp-tete">
        <h2><Icone nom="import" taille={20} /> Import SIG</h2>
        <p>
          Un fichier de géomètre entre au registre comme un <strong>levé</strong>, jamais
          comme une parcelle : il est déposé, contrôlé, puis décidé par un tiers.
          Ce que vous déposez ici n’est opposable à personne tant que cette décision
          n’est pas prise.
        </p>
      </header>

      {/* ── 1. le fichier ─────────────────────────────────────────────── */}
      <section className="imp-bloc">
        <h3>1 · Le fichier</h3>
        <label className="imp-depot">
          <input
            type="file" multiple accept=".shp,.dbf,.prj,.dxf"
            onChange={(e) => { void prendreFichiers(e.target.files); e.target.value = ''; }} />
          <Icone nom="import" taille={26} />
          <span>Choisir un <b>.shp</b> (avec son <b>.dbf</b> et son <b>.prj</b>) ou un <b>.dxf</b></span>
        </label>

        {Object.keys(nomsFichiers).length > 0 && (
          <ul className="imp-fichiers">
            {Object.entries(nomsFichiers).map(([ext, nom]) => (
              <li key={ext}><b>.{ext}</b> {nom}</li>
            ))}
          </ul>
        )}

        {format === 'shapefile' && !corps.prj && (
          /* ⚠️ LE `.prj` MANQUANT N'EST PAS UN DÉTAIL. Lire des mètres comme des
             degrés donne une longitude à six chiffres — refusée. Mais lire un
             système local comme du WGS 84 donne des degrés PLAUSIBLES, au large
             du golfe de Guinée : rien ne lève, et la parcelle est ailleurs. */
          <div className="imp-manque">
            <b>Pas de .prj.</b> Le registre ne devinera pas dans quel plan vos
            coordonnées sont écrites — un système supposé pose la parcelle ailleurs
            sans rien signaler. Indiquez-le explicitement :
            <select value={corps.epsg ?? ''} onChange={(e) => setCorps({ ...corps, epsg: e.target.value ? Number(e.target.value) : undefined })}>
              <option value="">— choisir le système —</option>
              {FUSEAUX.map((f) => <option key={f.epsg} value={f.epsg}>{f.libelle}</option>)}
            </select>
          </div>
        )}

        {format === 'dxf' && (
          <div className="imp-manque">
            <b>Un DXF ne porte aucun système de coordonnées.</b> Aucun — c’est le
            format qui est ainsi. Il faut le déclarer :
            <select value={corps.epsg ?? ''} onChange={(e) => setCorps({ ...corps, epsg: e.target.value ? Number(e.target.value) : undefined })}>
              <option value="">— choisir le système —</option>
              {FUSEAUX.map((f) => <option key={f.epsg} value={f.epsg}>{f.libelle}</option>)}
            </select>
          </div>
        )}

        {format && (
          <div className="imp-gestes">
            <button className="imp-principal" disabled={occupe || (format === 'dxf' && !corps.epsg)} onClick={() => void analyser()}>
              {occupe ? 'Lecture…' : 'Analyser — sans rien écrire'}
            </button>
            <button className="imp-discret" onClick={recommencer}>Recommencer</button>
          </div>
        )}
      </section>

      {erreur && (
        <div className="imp-refus">
          <Icone nom="alerte" taille={17} /> <b>{erreur}</b>
          {detail && <p>{detail}</p>}
        </div>
      )}

      {/* ── 2. ce que le fichier contient ─────────────────────────────── */}
      {analyse && (
        <section className="imp-bloc">
          <h3>2 · Ce que le fichier contient</h3>

          <div className="imp-chiffres">
            <div><b>{analyse.entites}</b><span>parcelle(s)</span></div>
            <div><b>{analyse.surfaceTotaleM2.toLocaleString('fr-FR')}</b><span>m² au total</span></div>
            <div className={analyse.entitesEnRecouvrement > 0 ? 'imp-mauvais' : ''}>
              <b>{analyse.entitesEnRecouvrement}</b><span>en recouvrement</span>
            </div>
            <div className={analyse.entitesInvalides > 0 ? 'imp-mauvais' : ''}>
              <b>{analyse.entitesInvalides}</b><span>géométrie(s) invalide(s)</span>
            </div>
          </div>

          <p className="imp-systeme">
            Système lu : <b>{analyse.systeme.nom}</b> (EPSG:{analyse.systeme.epsg})
            {analyse.systeme.origine === 'impose' && <em> — imposé par vous, pas lu dans le fichier</em>}
          </p>

          {analyse.avertissements.length > 0 && (
            <ul className="imp-avertissements">
              {analyse.avertissements.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          )}

          <div className="imp-defile">
            <table className="imp-table">
              <thead>
                <tr><th>Rang</th><th>Surface</th><th>Attributs</th><th>Recouvre</th></tr>
              </thead>
              <tbody>
                {analyse.apercu.map((e) => (
                  <tr key={e.rang} className={e.recouvrements.length > 0 ? 'imp-ligne-mauvaise' : ''}>
                    <td>{e.rang}</td>
                    <td>{e.surfaceM2.toLocaleString('fr-FR')} m²</td>
                    <td>{Object.entries(e.attributs).filter(([, v]) => v !== null)
                          .map(([k, v]) => `${k} : ${v}`).join(' · ') || <em>aucun</em>}</td>
                    <td>
                      {e.recouvrements.length === 0 ? <em>—</em> : e.recouvrements.map((r) => (
                        <button key={r.parcelle} className="imp-lien"
                                onClick={() => onOuvrirParcelle?.(r.parcelle)}>
                          {r.parcelle} ({r.partPct} %)
                        </button>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── 3. ce que le fichier NE dit pas ───────────────────────────── */}
      {analyse && (
        <section className="imp-bloc">
          <h3>3 · Ce que le fichier ne dit pas, et que vous déclarez</h3>
          <p className="imp-aide">
            Aucun des deux formats ne porte la méthode ni la précision. Votre
            déclaration est journalisée : un fichier reçu par courriel n’est pas
            un levé GNSS&nbsp;RTK.
          </p>
          <div className="imp-formulaire">
            <label>Référence du lot
              <input value={lotReference} onChange={(e) => setLotReference(e.target.value)}
                     placeholder="LOT-KALOUM-2026-014" />
              <em>Rejouer le même lot ne déposera rien deux fois.</em>
            </label>
            <label>Méthode du levé
              <select value={methode} onChange={(e) => setMethode(e.target.value as MethodeLeve)}>
                {METHODES.map((m) => <option key={m.cle} value={m.cle}>{m.libelle}</option>)}
              </select>
            </label>
            <label>Précision annoncée (m)
              <input type="number" step="0.01" min="0.01" value={precision}
                     onChange={(e) => setPrecision(e.target.value)} />
            </label>
            <label>Dossier (facultatif)
              <input value={dossier} onChange={(e) => setDossier(e.target.value)}
                     placeholder="GN-DOS-000000004" />
              <em>Le levé s’inscrit alors à l’étape « levé reçu ».</em>
            </label>
          </div>

          {analyse.entitesEnRecouvrement > 0 && (
            <label className="imp-malgre">
              <input type="checkbox" checked={malgreRecouvrements}
                     onChange={(e) => setMalgre(e.target.checked)} />
              <span>
                Déposer malgré les <b>{analyse.entitesEnRecouvrement}</b> recouvrement(s).
                Sans cette case, les parcelles qui recouvrent une parcelle active sont
                écartées du dépôt — les autres passent. Votre décision est journalisée.
              </span>
            </label>
          )}

          <div className="imp-gestes">
            <button className="imp-principal" disabled={!prêtÀDéposer || occupe} onClick={() => void deposer()}>
              {occupe ? 'Dépôt…' : `Déposer ${analyse.entites} levé(s)`}
            </button>
            {!prêtÀDéposer && lotReference.trim().length < 4 && (
              <span className="imp-aide">Une référence de lot d’au moins 4 caractères est requise.</span>
            )}
          </div>
        </section>
      )}

      {depot && (
        <div className="imp-fait">
          <Icone nom="valide" taille={17} /> {depot}
          <p>Ces levés attendent maintenant un contrôle et une décision — ils ne sont opposables à personne.</p>
          <button className="imp-discret" onClick={recommencer}>Importer un autre fichier</button>
        </div>
      )}

      {/* ── les lots déjà entrés ──────────────────────────────────────── */}
      <section className="imp-bloc">
        <h3>Lots déjà importés</h3>
        {lots.length === 0
          ? <p className="imp-aide">Aucun lot n’a encore été importé dans ce territoire.</p>
          : (
            <table className="imp-table">
              <thead><tr><th>Lot</th><th>Levés</th><th>Déposé le</th><th>Statuts</th></tr></thead>
              <tbody>
                {lots.map((l) => (
                  <tr key={l.lot}>
                    <td>{l.lot}</td><td>{l.leves}</td>
                    <td>{l.depose_le?.slice(0, 16)}</td><td>{l.statuts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </section>
    </div>
  );
}
